import React, { useEffect, useRef, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  errorToast,
  successToast,
  useOutsideClick,
  getFilterKey,
  isSuperAdmin
} from '../../../utils/helper';
import {
  getExerciseCategories,
  createExerciseCategory,
  deleteExerciseCategory
} from '../../../api/healthAPI';
import Breadcrumb from '../../../component/common/Breadcrumb';
import Table from '../../../component/common/Table';
import Pagination from '../../../component/common/Pagination/Pagination';
import { Helmet } from 'react-helmet';
import SearchInput from '../../../component/common/Input/SearchInput';
import { PER_PAGE, STATUS_FOR_DROPDOWN } from '../../../utils/constants';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import SelectMenu from '../../../component/common/SelectMenu';
import { XMarkIcon } from '@heroicons/react/20/solid';
import Loader from '../../../component/common/Loader';
import AddCategoryModal from '../../../component/common/modals/AddCategoryModal';
import { FilterPanel, TableCard} from '../../../component/common/designSystem';

const pages = [{ name: 'Workout Categories', href: '/health/categories' }];

const ExerciseCategories = () => {
  const wrapperRef = useRef(null);
  const [loader, setLoader] = useState(true);
  const [categoriesList, setCategoriesList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState(-1);
  const [adminNameList, setAdminNameList] = useState([]);
  const [sortBy, setSortBy] = useState('');
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE[0]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const [openAddCategory, setOpenAddCategory] = useState(false);
  const [filterCriteria, setFilterCriteria] = useState({
    accountType: '',
    categoryStatus: '',
    draft: ''
  });

  const handlePagination = async (pageNumber, pageSize, searchKey, query, sortBy, sortOrder) => {
    setLoader(true);
    try {
      const params = {
        page: pageNumber,
        limit: pageSize,
        search: searchKey || '',
        sortBy: sortBy || 'createdAt',
        sortOrder: sortOrder || -1,
        ...query
      };

      const response = await getExerciseCategories(params);

      if (response?.meta?.code === 200) {
        setCurrentPage(pageNumber);
        setCategoriesList(response?.data || []);
        setTotalCount(response?.meta?.totalCount || 0);
      } else {
        errorToast(response?.meta?.message || 'Failed to fetch categories');
        setCategoriesList([]);
        setTotalCount(0);
      }
    } catch (error) {
      errorToast('Failed to fetch categories');
      setCategoriesList([]);
      setTotalCount(0);
    } finally {
      setLoader(false);
    }
  };

  const handleAddCategory = async (categoryName) => {
    setLoader(true);
    try {
      const payload = {
        name: categoryName,
        contentType: 31
      };

      const response = await createExerciseCategory(payload);

      if (response?.meta?.code === 1) {
        successToast(response?.meta?.message || 'Category created successfully');
        setOpenAddCategory(false);
        // Refresh the categories list
        handlePagination(1, selectedPerPage?.value, searchTerm, filterQuery, sortBy, sortOrder);
      } else {
        errorToast(response?.meta?.message || 'Failed to create category');
      }
    } catch (error) {
      errorToast('Failed to create category');
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    // Initial load
    handlePagination(1, selectedPerPage?.value, '', '', sortBy, sortOrder);
  }, []);

  useEffect(() => {
    if (searchTerm) {
      let searchParam = searchTerm.trim();
      const delayDebounceFn = setTimeout(() => {
        handlePagination(1, selectedPerPage?.value, searchParam, filterQuery, sortBy, sortOrder);
      }, 800);

      return () => clearTimeout(delayDebounceFn);
    } else {
      handlePagination(1, selectedPerPage?.value, '', filterQuery, sortBy, sortOrder);
    }
  }, [searchTerm]);

  const handlePerPage = (perPage) => {
    setSelectedPerPage(perPage);
    handlePagination(1, perPage.value, searchTerm, filterQuery, sortBy, sortOrder);
  };

  const deleteHandler = async (id) => {
    setLoader(true);
    try {
      const response = await deleteExerciseCategory(id);
      if (response?.meta?.code === 1) {
        successToast('Category deleted successfully');
        handlePagination(1, selectedPerPage?.value, searchTerm, filterQuery, sortBy, sortOrder);
      } else {
        errorToast(response?.meta?.message || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      errorToast('Failed to delete category');
    } finally {
      setLoader(false);
    }
  };

  const refreshTable = () =>
    handlePagination(1, selectedPerPage?.value, searchTerm, filterQuery, sortBy, sortOrder);

  const handleSortBy = (sortByValue) => {
    setSortBy(sortByValue);
    if (sortByValue === sortBy) {
      let tempSortOrder = sortOrder === 1 ? -1 : 1;
      setSortOrder(tempSortOrder);
      handlePagination(
        currentPage,
        selectedPerPage?.value,
        searchTerm,
        filterQuery,
        sortByValue,
        tempSortOrder
      );
    } else {
      setSortOrder(1);
      handlePagination(
        currentPage,
        selectedPerPage?.value,
        searchTerm,
        filterQuery,
        sortByValue,
        1
      );
    }
  };

  useOutsideClick(wrapperRef, () => {
    if (showFilterModal) setShowFilterModal(!showFilterModal);
  });

  const filterHandler = (data, key) => {
    setFilterCriteria((prevData) => ({
      ...prevData,
      [key]: data
    }));
    let clone = JSON.parse(JSON.stringify(filterCriteria));
    const newData = { ...clone, [key]: data };
    let tempFilterQuery = [];
    Object.keys(newData).forEach(function (key) {
      if (newData[key]?.value?.toString()) {
        tempFilterQuery.push(`&${key}=${newData[key]?.value}`);
      }
    });
    let query = tempFilterQuery?.toString()?.replaceAll(',', '');
    setFilterQuery(query);
    handlePagination(1, selectedPerPage.value, searchTerm, query, sortBy, sortOrder);
  };

  const resetHandler = () => {
    setFilterCriteria({
      accountType: '',
      categoryStatus: ''
    });
    setFilterQuery('');
    setShowFilterModal(false);
    handlePagination(1, selectedPerPage.value, searchTerm, '', sortBy, sortOrder);
  };

  const columns = [
    {
      title: 'Category Name',
      key: 'name',
      sortKey: 'name',
      sortable: true,
      type: 'text',
      align: 'left',
      longText: true
    },

    {
      title: 'Created On',
      key: 'createdAt',
      sortable: true,
      type: 'date',
      align: 'left'
    },
    {
      title: 'Status',
      key: 'status',
      sortable: false,
      type: 'badge',
      align: 'left'
    }
    // {
    //   title: 'Action',
    //   key: 'action',
    //   type: 'action',
    //   align: 'right',
    //   isView: false, // Disable view for now
    //   isEdit: false, // Disable edit for now
    //   isDelete: true
    // }
  ];

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Workout Categories | Shoorah Admin</title>
      </Helmet>
      {loader ? <Loader /> : null}

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Workout Categories</h1>
      </div>

      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        <div className="sm:flex justify-between">
          <div className="flex">
            <div className="hidden sm:flex mr-3 self-center">
              <span className="self-center text-sm mr-2 text-shoorah-lightBlack dark:text-white">
                Per Page
              </span>
              <div className="w-[80px]">
                <SelectMenu
                  menuList={PER_PAGE}
                  showLabel={false}
                  defaultSelected={selectedPerPage}
                  setSelectedMenu={handlePerPage}
                />
              </div>
            </div>
            <div className="self-center">
              <SearchInput
                id="searchKey"
                name="searchKey"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by category name"
              />
            </div>
          </div>
          <div className="mt-3 sm:mt-0 flex justify-between">
            <div className="sm:hidden flex self-center">
              <span className="self-center hidden text-sm mr-2 dark:text-white">Per Page</span>
              <div className="w-16">
                <SelectMenu
                  menuList={PER_PAGE}
                  showLabel={false}
                  defaultSelected={selectedPerPage}
                  setSelectedMenu={handlePerPage}
                />
              </div>
            </div>
            <div className="flex gap-y-2 flex-wrap">
              <div className="ml-3 self-center">
                <button
                  onClick={() => setOpenAddCategory(true)}
                  type="button"
                  className="btn-primary sm:w-auto"
                >
                  Add New Category
                </button>
              </div>
              <div className="ml-3 self-center">
                <button
                  className="btn-primary sm:w-auto"
                  onClick={() => setShowFilterModal((state) => !state)}
                >
                  <FunnelIcon className="text-white w-[18px] h-[20px] inline" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <FilterPanel
          open={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          onReset={resetHandler}
        >
              <div>
                <SelectMenu
                  menuList={STATUS_FOR_DROPDOWN}
                  label="Category status"
                  isRequired={false}
                  defaultSelected={filterCriteria.categoryStatus}
                  setSelectedMenu={(data) => filterHandler(data, 'categoryStatus')}
                />
              </div>
            </FilterPanel>

        

        <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: selectedPerPage?.value,
            onPageChange: (page) =>
              handlePagination(
                page,
                selectedPerPage?.value,
                searchTerm,
                filterQuery,
                sortBy,
                sortOrder
              ),
            onPageSizeChange: (size) =>
              handlePerPage(PER_PAGE.find((o) => o.value === size) || PER_PAGE[0]),
            pageSizeOptions: PER_PAGE.map((o) => o.value)
          }}
        >
          <Table
            columns={columns}
            data={categoriesList}
            name={'categories_table'}
            setDeleteId={deleteHandler}
            bottomBorder={totalCount > selectedPerPage?.value}
            contentType={2}
            refreshTable={refreshTable}
            setSortBy={(sort) => handleSortBy(sort)}
            loader={loader}
            setSearchTerm={(data) => setSearchTerm(data)}
          />
        </TableCard>
      </div>

      

      {/* Add Category Modal */}
      {openAddCategory && (
        <AddCategoryModal
          open={openAddCategory}
          onClose={() => setOpenAddCategory(false)}
          onSubmit={handleAddCategory}
          title="Add New Exercise Category"
          placeholder="Enter category name"
        />
      )}
    </div>
  );
};

ExerciseCategories.propTypes = {};

export default ExerciseCategories;
